import { useState, useEffect } from 'react';
import inventoryService, { Product, Brand, Supplier, Category, InventoryStats } from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';

export const useInventory = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar todos los datos del inventario
  const loadInventoryData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [productsData, brandsData, suppliersData, categoriesData, statsData] = await Promise.all([
        inventoryService.getProducts(),
        inventoryService.getBrands(),
        inventoryService.getSuppliers(),
        inventoryService.getCategories(),
        inventoryService.getInventoryStats()
      ]);

      setProducts(productsData);
      setBrands(brandsData);
      setSuppliers(suppliersData);
      setCategories(categoriesData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando datos del inventario');
      console.error('Error cargando inventario:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar solo productos
  const loadProducts = async () => {
    if (!user) return;
    
    setLoading(true);
    console.log('🔄 useInventory: Iniciando carga de productos...');
    try {
      // Intentar cargar desde la estructura documentada primero
      console.log('🔄 useInventory: Llamando a inventoryService.getProductsFromDocumentedStructure()...');
      const productsData = await inventoryService.getProductsFromDocumentedStructure();
      console.log('✅ useInventory: Productos cargados:', productsData.length);
      console.log('📋 useInventory: Primeros 3 productos:', productsData.slice(0, 3).map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        imageURL: p.imageURL,
        sku: p.sku
      })));
      setProducts(productsData);
    } catch (err) {
      console.error('❌ useInventory: Error cargando productos:', err);
      setError(err instanceof Error ? err.message : 'Error cargando productos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar solo marcas
  const loadBrands = async () => {
    if (!user) return;
    
    try {
      // Intentar cargar desde la estructura documentada primero
      const brandsData = await inventoryService.getBrandsFromDocumentedStructure();
      setBrands(brandsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando marcas');
    }
  };

  // Cargar solo proveedores
  const loadSuppliers = async () => {
    if (!user) return;
    
    try {
      // Intentar cargar desde la estructura documentada primero
      const suppliersData = await inventoryService.getSuppliersFromDocumentedStructure();
      setSuppliers(suppliersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando proveedores');
    }
  };

  // Cargar solo categorías
  const loadCategories = async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Cargando categorías en useInventory...');
      // Intentar cargar desde la estructura documentada primero
      const categoriesData = await inventoryService.getCategoriesFromDocumentedStructure();
      console.log('📊 Categorías cargadas en useInventory:', categoriesData.length);
      console.log('📋 Lista de categorías:', categoriesData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('❌ Error cargando categorías en useInventory:', err);
      setError(err instanceof Error ? err.message : 'Error cargando categorías');
    }
  };

  // Cargar estadísticas
  const loadStats = async () => {
    if (!user) return;
    
    try {
      const statsData = await inventoryService.getInventoryStats();
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando estadísticas');
    }
  };

  // Operaciones CRUD para productos
  const addProduct = async (productData: Omit<Product, 'id' | 'sku' | 'createdAt' | 'updatedAt'>) => {
    if (!user) return;
    
    try {
      const productId = await inventoryService.addProduct(productData);
      await loadProducts(); // Recargar productos
      await loadStats(); // Actualizar estadísticas
      return productId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error agregando producto');
      throw err;
    }
  };

  const updateProduct = async (productId: string, productData: Partial<Product>) => {
    if (!user) return;
    
    try {
      await inventoryService.updateProduct(productId, productData);
      await loadProducts(); // Recargar productos
      await loadStats(); // Actualizar estadísticas
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando producto');
      throw err;
    }
  };

  const updateProductWithVariants = async (
    sku: string, 
    categoryName: string, 
    productData: any, 
    variants: any[]
  ) => {
    if (!user) return;
    
    try {
      await inventoryService.updateProductWithVariants(sku, categoryName, productData, variants);
      await loadProducts(); // Recargar productos
      await loadStats(); // Actualizar estadísticas
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando producto con variantes');
      throw err;
    }
  };

  const uploadProductImage = async (
    file: File,
    sku: string,
    categoryName: string,
    color: string,
    imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3' | 'closeup4'
  ) => {
    if (!user) return { success: false, error: 'Usuario no autenticado' };
    
    try {
      return await inventoryService.uploadProductImage(file, sku, categoryName, color, imageType);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error subiendo imagen');
      return { success: false, error: err instanceof Error ? err.message : 'Error desconocido' };
    }
  };

  const deleteProduct = async (productId: string) => {
    if (!user) return;
    
    try {
      await inventoryService.deleteProduct(productId);
      await loadProducts(); // Recargar productos
      await loadStats(); // Actualizar estadísticas
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error eliminando producto');
      throw err;
    }
  };

  // Operaciones CRUD para marcas
  const addBrand = async (brandData: Omit<Brand, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) return;
    
    try {
      const brandId = await inventoryService.addBrand(brandData);
      await loadBrands(); // Recargar marcas
      return brandId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error agregando marca');
      throw err;
    }
  };

  const updateBrand = async (brandId: string, brandData: Partial<Brand>) => {
    if (!user) return;
    
    try {
      await inventoryService.updateBrand(brandId, brandData);
      await loadBrands(); // Recargar marcas
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando marca');
      throw err;
    }
  };

  const deleteBrand = async (brandId: string) => {
    if (!user) return;
    
    try {
      await inventoryService.deleteBrand(brandId);
      await loadBrands(); // Recargar marcas
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error eliminando marca');
      throw err;
    }
  };

  // Operaciones CRUD para proveedores
  const addSupplier = async (supplierData: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) return;
    
    try {
      const supplierId = await inventoryService.addSupplier(supplierData);
      await loadSuppliers(); // Recargar proveedores
      return supplierId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error agregando proveedor');
      throw err;
    }
  };

  const updateSupplier = async (supplierId: string, supplierData: Partial<Supplier>) => {
    if (!user) return;
    
    try {
      await inventoryService.updateSupplier(supplierId, supplierData);
      await loadSuppliers(); // Recargar proveedores
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando proveedor');
      throw err;
    }
  };

  const deleteSupplier = async (supplierId: string) => {
    if (!user) return;
    
    try {
      await inventoryService.deleteSupplier(supplierId);
      await loadSuppliers(); // Recargar proveedores
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error eliminando proveedor');
      throw err;
    }
  };

  // Operaciones CRUD para categorías
  const addCategory = async (categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user) return;
    
    try {
      const categoryId = await inventoryService.addCategory(categoryData);
      await loadCategories(); // Recargar categorías
      return categoryId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error agregando categoría');
      throw err;
    }
  };

  const updateCategory = async (categoryId: string, categoryData: Partial<Category>) => {
    if (!user) return;
    
    try {
      await inventoryService.updateCategory(categoryId, categoryData);
      await loadCategories(); // Recargar categorías
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error actualizando categoría');
      throw err;
    }
  };

  const deleteCategory = async (categoryId: string) => {
    if (!user) return;
    
    try {
      await inventoryService.deleteCategory(categoryId);
      await loadCategories(); // Recargar categorías
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error eliminando categoría');
      throw err;
    }
  };

  // Búsqueda de productos
  const searchProducts = async (searchTerm: string) => {
    if (!user) return [];
    
    try {
      return await inventoryService.searchProducts(searchTerm);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error buscando productos');
      return [];
    }
  };

  // Obtener productos con stock bajo
  const getLowStockProducts = async (threshold: number = 5) => {
    if (!user) return [];
    
    try {
      return await inventoryService.getLowStockProducts(threshold);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error obteniendo productos con stock bajo');
      return [];
    }
  };

  // Cargar datos iniciales cuando el usuario cambie
  useEffect(() => {
    if (user) {
      loadInventoryData();
    } else {
      // Limpiar datos cuando el usuario se desloguea
      setProducts([]);
      setBrands([]);
      setSuppliers([]);
      setCategories([]);
      setStats(null);
      setError(null);
    }
  }, [user]);

  return {
    // Estado
    products,
    brands,
    suppliers,
    categories,
    stats,
    loading,
    error,
    
    // Funciones de carga
    loadInventoryData,
    loadProducts,
    loadBrands,
    loadSuppliers,
    loadCategories,
    loadStats,
    
    // Operaciones CRUD - Productos
    addProduct,
    updateProduct,
    updateProductWithVariants,
    uploadProductImage,
    deleteProduct,
    
    // Operaciones CRUD - Marcas
    addBrand,
    updateBrand,
    deleteBrand,
    
    // Operaciones CRUD - Proveedores
    addSupplier,
    updateSupplier,
    deleteSupplier,
    
    // Operaciones CRUD - Categorías
    addCategory,
    updateCategory,
    deleteCategory,
    
    // Funciones de búsqueda y filtrado
    searchProducts,
    getLowStockProducts,
    
    // Utilidades
    clearError: () => setError(null)
  };
};

export default useInventory;
